import { supabase } from '@/lib/supabase';

export interface TemplateSet {
  id: string;
  name: string;
  description: string;
  category: string;
  scope: 'global' | 'tenant';
  is_active: boolean;
  owner_tenant_id: string | null;
  created_at: string;
  updated_at: string;
  template_count?: number;
}

export interface TemplateSetItem {
  id: string;
  template_set_id: string;
  notification_type: string;
  title_template: string;
  message_template: string;
  email_subject_template: string | null;
  email_body_template: string | null;
  sms_template: string | null;
  slack_template: string | null;
  description: string | null;
  variables: string[];
  created_at: string;
  updated_at: string;
}

export interface NotificationTemplate {
  id: string;
  tenant_id: string;
  notification_type: string;
  title_template: string;
  message_template: string;
  email_subject_template: string | null;
  email_body_template: string | null;
  sms_template: string | null;
  slack_template: string | null;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export class TemplateSetService {
  /**
   * Check if current user is a superadmin
   */
  static async isCurrentUserSuperadmin(): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return false;

      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          role_id,
          roles (
            id,
            name
          )
        `)
        .eq('user_id', user.user.id);

      if (!userRoles || userRoles.length === 0) return false;

      return userRoles.some(roleData => {
        const role = Array.isArray(roleData.roles) ? roleData.roles[0] : roleData.roles;
        return roleData.role_id === 42 || role?.name === 'Superadmin';
      });
    } catch (error) {
      console.error('Error checking superadmin status:', error);
      return false;
    }
  }
  /**
   * Get all available template sets (global + tenant-specific) with template counts
   */
  static async getAvailableTemplateSets(): Promise<TemplateSet[]> {
    const { data, error } = await supabase
      .from('notification_template_sets')
      .select(`
        *,
        notification_template_set_items (count)
      `)
      .eq('is_active', true)
      .order('scope', { ascending: false }) // Global first
      .order('name');

    if (error) {
      console.error('Error fetching template sets:', error);
      throw error;
    }

    // Add template count to each set
    const setsWithCounts = ((data || []) as unknown as Array<Record<string, unknown> & { notification_template_set_items?: Array<{ count?: number }> }>).map((set) => ({
      ...set,
      template_count: set.notification_template_set_items?.[0]?.count || 0
    }));

    return setsWithCounts as unknown as TemplateSet[];
  }

  /**
   * Get items for a specific template set
   */
  static async getTemplateSetItems(templateSetId: string): Promise<TemplateSetItem[]> {
    const { data, error } = await supabase
      .from('notification_template_set_items')
      .select('*')
      .eq('template_set_id', templateSetId)
      .order('notification_type');

    if (error) {
      console.error('Error fetching template set items:', error);
      throw error;
    }

    return (data || []) as unknown as TemplateSetItem[];
  }

  /**
   * Load a template set by copying its items to the current tenant's notification_templates
   */
  static async loadTemplateSet(templateSetId: string, currentTenantId?: string): Promise<void> {
    
    // Get current user
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error('User not authenticated');
    }

    // Use provided currentTenantId or fall back to user's tenant_id
    let tenantId = currentTenantId;
    
    if (!tenantId) {
      const { data: userProfile } = await supabase
        .from('users')
        .select('tenant_id')
        .eq('id', user.user.id)
        .single();

      if (!userProfile?.tenant_id) {
        throw new Error('User tenant not found');
      }
      
      tenantId = userProfile.tenant_id;
    }

    // Get template set items
    const templateItems = await this.getTemplateSetItems(templateSetId);

    if (templateItems.length === 0) {
      throw new Error('No templates found in this set');
    }


    // Transform template set items to notification templates
    const notificationTemplates = templateItems.map(item => ({
      tenant_id: tenantId,
      notification_type: item.notification_type,
      title_template: item.title_template,
      message_template: item.message_template,
      email_subject_template: item.email_subject_template,
      email_body_template: item.email_body_template,
      sms_template: item.sms_template,
      slack_template: item.slack_template,
      variables: item.variables,
      is_active: true
    }));

    // Delete existing templates for this tenant first, then insert all new ones
    // This ensures we get all 23 templates, not just updates to existing ones
    const { data: deleteData, error: deleteError } = await supabase
      .from('notification_templates')
      .delete()
      .eq('tenant_id', tenantId);

    if (deleteError) {
      console.error('Error deleting existing templates:', deleteError);
      throw deleteError;
    }

    // Insert all templates from the template set
    const { data: insertData, error } = await supabase
      .from('notification_templates')
      .insert(notificationTemplates)
      .select();

    if (error) {
      console.error('Error loading template set:', error);
      throw error;
    }
  }

  /**
   * Get current tenant's notification templates
   */
  static async getCurrentTemplates(): Promise<NotificationTemplate[]> {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error('User not authenticated');
    }

    const { data: userProfile } = await supabase
      .from('users')
      .select('tenant_id')
      .eq('id', user.user.id)
      .single();

    if (!userProfile?.tenant_id) {
      throw new Error('User tenant not found');
    }

    const { data, error } = await supabase
      .from('notification_templates')
      .select('*')
      .eq('tenant_id', userProfile.tenant_id)
      .order('notification_type');

    if (error) {
      console.error('Error fetching current templates:', error);
      throw error;
    }

    return (data || []) as unknown as NotificationTemplate[];
  }

  /**
   * Preview a template set without loading it
   */
  static async previewTemplateSet(templateSetId: string): Promise<TemplateSetItem[]> {
    return this.getTemplateSetItems(templateSetId);
  }

  /**
   * Update a specific template set item
   */
  static async updateTemplateSetItem(
    itemId: string,
    updates: Partial<Omit<TemplateSetItem, 'id' | 'template_set_id' | 'created_at' | 'updated_at'>>
  ): Promise<void> {
    const { error } = await supabase
      .from('notification_template_set_items')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', itemId);

    if (error) {
      console.error('Error updating template set item:', error);
      throw error;
    }
  }

  /**
   * Create a new template set from current tenant's templates
   */
  static async createTemplateSetFromCurrent(name: string, description: string): Promise<TemplateSet> {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error('User not authenticated');
    }

    const { data: userProfile } = await supabase
      .from('users')
      .select('tenant_id')
      .eq('id', user.user.id)
      .single();

    if (!userProfile?.tenant_id) {
      throw new Error('User tenant not found');
    }

    // Create the template set
    const { data: templateSet, error: setError } = await supabase
      .from('notification_template_sets')
      .insert({
        name,
        description,
        category: 'custom',
        scope: 'tenant',
        is_active: true,
        owner_tenant_id: userProfile.tenant_id
      })
      .select()
      .single();

    if (setError) {
      console.error('Error creating template set:', setError);
      throw setError;
    }

    // Get current templates
    const currentTemplates = await this.getCurrentTemplates();

    if (currentTemplates.length === 0) {
      throw new Error('No templates found to create set from');
    }

    // Transform current templates to template set items
    const templateSetItems = currentTemplates.map(template => ({
      template_set_id: templateSet.id,
      notification_type: template.notification_type,
      title_template: template.title_template,
      message_template: template.message_template,
      email_subject_template: template.email_subject_template,
      email_body_template: template.email_body_template,
      sms_template: template.sms_template,
      slack_template: template.slack_template,
      description: `Template for ${template.notification_type}`,
      variables: template.variables
    }));

    // Insert template set items
    const { error: itemsError } = await supabase
      .from('notification_template_set_items')
      .insert(templateSetItems);

    if (itemsError) {
      console.error('Error creating template set items:', itemsError);
      throw itemsError;
    }

    return templateSet as unknown as TemplateSet;
  }
}